import { ListKeyManager, ListKeyManagerOption } from '@angular/cdk/a11y';
import { AfterViewInit, Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import * as Constants from '@emi/shared/constants';
import { ConfigureData, DateRange, FileTypes, Frequency, PricePer, SearchOption, TimeSeriesData } from '@emi/shared/models';
import {
    DataExtractionState,
    addConfigureDataSelection,
    addDateRangeSelection,
    getCountries,
    getExchangeRates,
    getSelectedConfigureData,
} from '@emi/shared/state/filters';
import { SkuMetadataState, fetchSkuMetadata, getSkuMetadataState } from '@emi/shared/state/sku-metadata';
import { SkuSearchState, getSkuSearchResults, selectSkuCount, selectSkuSearchState, setCurrency } from '@emi/shared/state/sku-search';
import { ProgressBarHandler } from '@emi/shared/ui';
import { Store, select } from '@ngrx/store';
import * as moment from 'moment';
import { Subscription } from 'rxjs';

import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';

const DATE_PICKER_FORMATS = {
    parse: {
        dateInput: 'LL',
    },
    display: {
        dateInput: 'DD MMM YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'YYYY',
    },
};

@Component({
    selector: 'via-configure-data',
    templateUrl: './configure-data.component.html',
    styleUrls: ['./configure-data.component.scss'],
    providers: [
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        { provide: MAT_DATE_FORMATS, useValue: DATE_PICKER_FORMATS },
    ],
})
export class ConfigureDataComponent implements AfterViewInit, OnDestroy, OnInit {
    @ViewChildren('dataTypeCheckbox')
    public dataTypeCheckboxElements: QueryList<ListKeyManagerOption>;
    private skuSearchSubscription: Subscription;

    public configureData = new ConfigureData();
    public skuPriceCheckbox = false;
    public regularPriceCheckbox = false;
    public dailySkuPromotionValueCheckBox = false;
    public dailyInStockCheckBox = false;
    public ratingCheckbox = false;
    public numberOfReviewsCheckbox = false;
    public attributesCheckbox = false;
    public disableAttributeCheckBox = false;
    public dataType: Array<TimeSeriesData> = [];
    public pricePoints: Array<PricePer> = Constants.pricePoints;
    public fileTypes: Array<FileTypes> = Constants.fileTypes;
    public dateRangeTooltipText = Constants.Tooltips.DATE_RANGE;
    public frequencyTooltipText = Constants.Tooltips.FREQUENCY;
    public dataTypeTooltipText = Constants.Tooltips.DATA_TYPE;
    public pricePointTooltipText = Constants.Tooltips.PRICE_POINT;
    public currencyTooltipText = Constants.Tooltips.CURRENCY;
    public fileTypeTooltipText = Constants.Tooltips.FILE_TYPE;
    public skuCountMessage = '';
    public attributesMessage = '';
    public keyManager: ListKeyManager<any>;
    public highlightNextStep: boolean;
    public modalType = Constants.LocalStorageValues.CANCEL_MODAL;
    public dailyFrequencySelected: boolean;
    public selectedCurrency: string;
    public searchTerm = '';
    public searchOption: SearchOption;
    public includeSearchPhrase: boolean;

    private configureDataSubscription: Subscription;
    private skuCountSubscription: Subscription;
    private skuMetadataSubscription: Subscription;

    public dateRange = new FormGroup({
        startDate: new FormControl(),
        endDate: new FormControl(),
    });

    public minDate: Date;
    public maxDate: Date;
    public skuCount = 0;
    public maxSelectableDays = 0;

    public pricePointOptions: { value: PricePer, label: string, disabled: boolean }[] = [
        { value: 'Sku', label: 'SKU', disabled: false },
        { value: 'Unit', label: 'Unit', disabled: false },
    ];

    constructor(
        public dataExtractionStore: Store<DataExtractionState>,
        private progressBarHandler: ProgressBarHandler,
        private skuMetadataStore: Store<SkuMetadataState>,
        private skuSearchStore: Store<SkuSearchState>,
        private store: Store
    ) {
        this.progressBarHandler.highlightSubject.subscribe((x) => (this.highlightNextStep = x));

        this.skuMetadataStore.dispatch(fetchSkuMetadata());

        this.minDate = moment().utc().subtract(Constants.DatePicker.MAX_ALLOWED_YEARS_AGO, 'years').startOf('year').toDate();

        this.skuMetadataSubscription = this.skuMetadataStore.pipe(select(getSkuMetadataState)).subscribe((skuMetadata) => {
            if (skuMetadata.lastUpdated) {
                this.maxDate = skuMetadata.lastUpdated;
                return;
            }
            this.maxDate = moment().utc().subtract(Constants.DatePicker.DEFAULT_MAX_DATE_DAYS_AGO, 'days').toDate();
        });
    }

    public ngOnInit() {
        this.store.dispatch(getCountries());

        this.configureDataSubscription = this.dataExtractionStore.pipe(select(getSelectedConfigureData)).subscribe((x) => {
            this.configureData = x;
            this.selectedCurrency = x.currency;
            this.updateCheckbox(x.dataType);
            this.isAttributeChecked(x.fileType);
            this.attributesCheckbox = x.includeAttributesFile;
            this.dailyFrequencySelected = x.frequency === 'Daily';
            this.dataType = x.dataType;
            this.dateRange.controls.startDate.setValue(this.configureData.startDate);
            this.dateRange.controls.endDate.setValue(this.configureData.endDate);
        });

        this.skuSearchSubscription = this.store.pipe(select(selectSkuSearchState)).subscribe((state) => {
            this.searchTerm = state.searchTerm;
            this.searchOption = state.searchOption;
            this.includeSearchPhrase = state.includeSearchPhrase || false;
        });

        if (!this.dateRange.controls.startDate.value || !this.dateRange.controls.endDate.value) {
            this.selectDefaultDateRange();
        }

        this.skuCountSubscription = this.skuSearchStore.pipe(select(selectSkuCount)).subscribe((skuCount) => (this.skuCount = skuCount));

        this.maxSelectableDays = this.getMaxSelectableDays();
        this.dataExtractionStore.dispatch(getExchangeRates({ currency: this.selectedCurrency }));
    }

    public ngAfterViewInit(): void {
        this.keyManager = new ListKeyManager(this.dataTypeCheckboxElements).withWrap();
    }

    public updateFrequency(frequency: Frequency): void {
        this.updateDateRange(undefined, undefined);
        const configureData = { ...this.configureData, frequency: frequency };
        this.dailyFrequencySelected = frequency === 'Daily';
        this.dataExtractionStore.dispatch(addConfigureDataSelection({ configureData }));
        this.maxSelectableDays = this.getMaxSelectableDays();
    }

    private getMaxSelectableDays(): number {
        const dateRangeLimit = Constants.DatePicker.DATE_RANGE_LIMITS.find((limit) => {
            return (
                limit.frequency === this.configureData.frequency &&
                this.skuCount >= limit.skuCountLower &&
                this.skuCount <= limit.skuCountUpper
            );
        });

        if (dateRangeLimit) {
            this.skuCountMessage = dateRangeLimit.message;
            return dateRangeLimit.maxSelectableDays;
        }

        this.skuCountMessage = '';

        return 0;
    }

    public updateSkuPrice() {
        if (this.skuPriceCheckbox) {
            this.dataType = [...this.dataType, 'SkuPrice'];
        } else {
            this.dataType = this.dataType.filter((x) => x !== 'SkuPrice');
        }
        const configureData = {
            ...this.configureData,
            dataType: this.dataType,
        };
        this.dataExtractionStore.dispatch(addConfigureDataSelection({ configureData }));
    }

    public updateRegularPrice() {
        if (this.regularPriceCheckbox) {
            this.dataType = [...this.dataType, 'RegularPrice'];
        } else {
            this.dataType = this.dataType.filter((x) => x !== 'RegularPrice');
        }
        const configureData = {
            ...this.configureData,
            dataType: this.dataType,
        };
        this.dataExtractionStore.dispatch(addConfigureDataSelection({ configureData }));
    }

    public updatePromotion() {
        if (this.dailySkuPromotionValueCheckBox) {
            this.dataType = [...this.dataType, 'DailySkuPromotionValue'];
        } else {
            this.dataType = this.dataType.filter((x) => x !== 'DailySkuPromotionValue');
        }
        const configureData = {
            ...this.configureData,
            dataType: this.dataType,
        };
        this.dataExtractionStore.dispatch(addConfigureDataSelection({ configureData }));
    }

    public updateDailyStock() {
        if (this.dailyInStockCheckBox) {
            this.dataType = [...this.dataType, 'DailyInStock'];
        } else {
            this.dataType = this.dataType.filter((x) => x !== 'DailyInStock');
        }

        const configureData = {
            ...this.configureData,
            dataType: this.dataType,
        };
        this.dataExtractionStore.dispatch(addConfigureDataSelection({ configureData }));
    }

    public updateRating() {
        if (this.ratingCheckbox) {
            this.dataType = [...this.dataType, 'Rating'];
        } else {
            this.dataType = this.dataType.filter((x) => x !== 'Rating');
        }
        const configureData = {
            ...this.configureData,
            dataType: this.dataType,
        };
        this.dataExtractionStore.dispatch(addConfigureDataSelection({ configureData }));
    }

    public updateNumberOfReviews() {
        if (this.numberOfReviewsCheckbox) {
            this.dataType = [...this.dataType, 'NumberOfReviews'];
        } else {
            this.dataType = this.dataType.filter((x) => x !== 'NumberOfReviews');
        }
        const configureData = {
            ...this.configureData,
            dataType: this.dataType,
        };
        this.dataExtractionStore.dispatch(addConfigureDataSelection({ configureData }));
    }

    public updateAttributes() {
        const configureData = {
            ...this.configureData,
            includeAttributesFile: this.attributesCheckbox,
        };
        this.dataExtractionStore.dispatch(addConfigureDataSelection({ configureData }));
    }

    public updateFileType(matRadioChange: MatRadioChange) {
        // to be removed once excel can do attributes
        this.isAttributeChecked(matRadioChange.value);

        const configureData = {
            ...this.configureData,
            fileType: this.fileTypes.find((fileType) => fileType === matRadioChange.value) ?? Constants.Application.DEFAULT_FILE_TYPE,
            includeAttributesFile: this.attributesCheckbox,
        };
        this.dataExtractionStore.dispatch(addConfigureDataSelection({ configureData }));
    }

    public isAttributeChecked(value: string) {
        if (value == 'Excel') {
            this.attributesCheckbox = false;
            this.disableAttributeCheckBox = true;
            this.attributesMessage = Constants.GlobalVariables.ATTRIBUTES_NOT_ALLOWED;
        }
        if (value == 'CSV') {
            this.attributesCheckbox = false;
            this.disableAttributeCheckBox = false;
            this.attributesMessage = ' ';
        }
    }

    public isFileTypeChecked(fileType: FileTypes) {
        if (this.configureData.fileType === fileType) {
            return true;
        }
        return false;
    }

    public updateCheckbox(dataType: TimeSeriesData[]) {
        dataType.forEach((x) => {
            switch (x) {
                case 'SkuPrice':
                    this.skuPriceCheckbox = true;
                    break;
                case 'RegularPrice':
                    this.regularPriceCheckbox = true;
                    break;
                case 'DailySkuPromotionValue':
                    this.dailySkuPromotionValueCheckBox = true;
                    break;
                case 'DailyInStock':
                    this.dailyInStockCheckBox = true;
                    break;
                case 'Rating':
                    this.ratingCheckbox = true;
                    break;
                case 'NumberOfReviews':
                    this.numberOfReviewsCheckbox = true;
            }
        });
    }

    public updatePricePoint(matRadioChange: MatRadioChange) {
        const configureData = {
            ...this.configureData,
            pricePoint: this.pricePoints.find((pricePer) => pricePer === matRadioChange.value) ?? Constants.Application.DEFAULT_PRICE_PER,
        };
        this.dataExtractionStore.dispatch(addConfigureDataSelection({ configureData }));
    }

    public isPricePointChecked(pricePoint: PricePer) {
        if (this.configureData.pricePoint === pricePoint) {
            return true;
        }
        return false;
    }

    public updateCurrency(currency: string) {
        const configureData = { ...this.configureData, currency: currency };
        this.dataExtractionStore.dispatch(addConfigureDataSelection({ configureData }));
        this.dataExtractionStore.dispatch(getExchangeRates({ currency }));
        this.store.dispatch(setCurrency({ currency: currency }));

        this.store.dispatch(
            getSkuSearchResults({
                searchTerm: this.searchTerm,
                searchOption: {
                    label: this.searchOption.label,
                    value: this.searchOption.value,
                    searchDimension: this.searchOption.label,
                },
                includeSearchPhrase: this.includeSearchPhrase,
            })
        );
    }

    public onDateRangeChange(): void {
        this.updateDateRange(this.dateRange.controls.startDate.value, this.dateRange.controls.endDate.value);
        
        this.store.dispatch(
            getSkuSearchResults({
                searchTerm: this.searchTerm,
                searchOption: {
                    label: this.searchOption.label,
                    value: this.searchOption.value,
                    searchDimension: this.searchOption.label,
                },
                includeSearchPhrase: this.includeSearchPhrase,
            })
        );
    }

    public isPriceInputDisabled(): boolean {
        return !(this.skuPriceCheckbox || this.regularPriceCheckbox);
    }

    public isNextButtonHidden(): boolean {
        const skuCountLimit = Constants.Application.MAX_SELECTABLE_SKUS.value;
        if (this.skuCount > skuCountLimit) {
            this.skuCountMessage = Constants.Application.MAX_SELECTABLE_SKUS.message;
        }
        return !(
            (this.skuPriceCheckbox ||
                this.regularPriceCheckbox ||
                this.dailySkuPromotionValueCheckBox ||
                this.dailyInStockCheckBox ||
                this.ratingCheckbox ||
                this.numberOfReviewsCheckbox) &&
            this.dateRange.controls.startDate.value &&
            this.dateRange.controls.endDate.value &&
            this.skuCount < skuCountLimit &&
            this.isDateRangeValid(this.dateRange.controls.startDate.value, this.dateRange.controls.endDate.value)
        );
    }

    public dataTypeCheckboxKeyEvents(event: KeyboardEvent): void {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            this.keyManager.onKeydown(event);
            this.keyManager.activeItem.focus();
        }
    }

    public setFirstItemActive(): void {
        this.keyManager.setFirstItemActive();
    }

    private updateDateRange(startDate: Date | undefined, endDate: Date | undefined) {
        let dateRangeValue: DateRange | null;
        if (startDate && endDate) {
            dateRangeValue = new DateRange(startDate, endDate);
        } else {
            dateRangeValue = null;
        }
        this.dataExtractionStore.dispatch(addDateRangeSelection({ dateRange: dateRangeValue }));

        const configureData = { ...this.configureData, startDate: startDate, endDate: endDate, dateRange: dateRangeValue };
        this.dataExtractionStore.dispatch(addConfigureDataSelection({ configureData }));
    }

    private isDateRangeValid(startDate: Date, endDate: Date): boolean {
        if (!startDate || !endDate) {
            return false;
        }

        const numberOfDaysSelected = this.countDaysSelected(startDate, endDate);
        this.maxSelectableDays = this.getMaxSelectableDays();
        return (
            !(this.configureData.frequency === 'Daily') ||
            (this.configureData.frequency === 'Daily' && numberOfDaysSelected <= this.maxSelectableDays)
        );
    }

    private countDaysSelected(startDate: Date, endDate: Date): number {
        const includeStartAndEndDay = 1;
        return moment(endDate).diff(moment(startDate), 'days') + includeStartAndEndDay;
    }

    private selectDefaultDateRange(): void {
        const defaultDateRange = DateRange.getDefaultDateRange(this.maxDate);

        this.dateRange.controls.startDate.setValue(defaultDateRange.startDate);
        this.dateRange.controls.endDate.setValue(defaultDateRange.endDate);

        this.updateDateRange(defaultDateRange.startDate!, defaultDateRange.endDate!);
    }

    public ngOnDestroy(): void {
        this.configureDataSubscription.unsubscribe();
        this.skuCountSubscription.unsubscribe();
        this.skuMetadataSubscription.unsubscribe();
    }
}
